import type { InterfaceAddress } from '@interfaces/InterfaceAddress.ts';
import type { InterfaceDelivery } from '@interfaces/InterfaceDelivery.ts';
import type { InterfaceDeliveryContacts } from '@interfaces/InterfaceDeliveryContacts.ts';
import type { InterfaceDeliveryLocationHistory } from '@interfaces/InterfaceDeliveryLocationHistory.ts';
import type { InterfaceDeliveryQuote } from '@interfaces/InterfaceDeliveryQuote.ts';
import type { InterfaceDeliveryStatus } from '@interfaces/InterfaceDeliveryStatus.ts';
import type { InterfaceDeliveryStep } from '@interfaces/InterfaceDeliveryStep.ts';
import type { InterfaceDeliveryStepTask } from '@interfaces/InterfaceDeliveryStepTask.ts';
import type { InterfaceDeliveryStepTaskDoc } from '@interfaces/InterfaceDeliveryStepTaskDoc.ts';
import type { InterfaceGenericMap } from '@interfaces/InterfaceGenericMap.ts';
import type { InterfaceGenericTypeSlug } from '@interfaces/InterfaceGenericTypeSlug.ts';
import type { InterfaceInterfaceDeliveryVehicleDrivers } from '@interfaces/InterfaceInterfaceDeliveryVehicleDrivers.ts';
import type { InterfaceParcel } from '@interfaces/InterfaceParcel.ts';
import {
  createArraySelector,
  createFilteredSelector,
  createSmartSelector,
  sortByDate,
} from '@lib/shared-tooling/redux-toolkit-helpers/selector-factory.ts';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore.ts';
import { meLogOut } from '@store/reducers/meReducer.ts';
import { addSuccessNotification } from '@store/reducers/notificationReducer.ts';
import createDebouncedAsyncThunk from '@store/reducers/reducerHelpers/createDebouncedAsyncThunk.ts';
import { sliceStateCheckAndUpdate } from '@store/reducers/reducerHelpers/sliceStateCheckAndUpdate.ts';
import { thunkDelete } from '@store/reducers/reducerHelpers/thunkDelete.ts';
import { thunkGet } from '@store/reducers/reducerHelpers/thunkGet.ts';
import { thunkGetPaginated } from '@store/reducers/reducerHelpers/thunkGetPaginated.ts';
import { defaultTypeDebounceTimeMs } from '@store/reducers/reducerHelpers/thunkHelperShared.ts';
import { thunkPost } from '@store/reducers/reducerHelpers/thunkPost.ts';
import { thunkPut } from '@store/reducers/reducerHelpers/thunkPut.ts';

// Helper function to convert array to map using uuid as key
const arrayToMap = <T extends { uuid: string }>(array: T[]): InterfaceGenericMap<T> => {
  const map: InterfaceGenericMap<T> = {};
  if (Array.isArray(array)) {
    array.forEach((item) => {
      if (item.uuid) {
        map[item.uuid] = item;
      }
    });
  }
  return map;
};

// Helper to handle both array and map payloads
const handleArrayOrMapPayload = <T extends { uuid: string }>(
  state: DeliveryReducerState,
  action: { payload: T[] | InterfaceGenericMap<T> },
  stateKey: keyof DeliveryReducerState,
) => {
  if (Array.isArray(action.payload)) {
    const map = arrayToMap(action.payload);
    // logger.info('[deliveryReducer]', `[deliveryReducer] Converting array to map for ${stateKey}:`, {
    //   arrayLength: action.payload.length,
    //   mapKeys: Object.keys(map).length,
    //   firstItem: action.payload[0],
    // });
    (state[stateKey] as InterfaceGenericMap<T>) = map;
  } else if (action.payload && typeof action.payload === 'object') {
    // logger.info('[deliveryReducer]', `[deliveryReducer] Using map directly for ${stateKey}:`, {
    //   keys: Object.keys(action.payload).length,
    // });
    (state[stateKey] as InterfaceGenericMap<T>) = action.payload;
  }
};

export interface DeliveryReducerState {
  deliveryCancelTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  deliveryLiveStatusTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  deliveryScheduleWindowTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  deliveryQuoteStatusTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  deliveryStepTasksTypes: InterfaceGenericMap<InterfaceGenericTypeSlug>;
  deliveries: InterfaceGenericMap<InterfaceDelivery>;
  deliverySteps: InterfaceGenericMap<InterfaceDeliveryStep>;
  deliveryStepTasks: InterfaceGenericMap<InterfaceDeliveryStepTask>;
  deliveryStatuses: InterfaceGenericMap<InterfaceDeliveryStatus>;
  deliveryStepTaskDocuments: InterfaceGenericMap<InterfaceDeliveryStepTaskDoc>;
  deliveryParcels: InterfaceGenericMap<InterfaceParcel>;
  deliveryAddresses: InterfaceGenericMap<InterfaceAddress>;
  deliveryContacts: InterfaceGenericMap<InterfaceDeliveryContacts>;
  deliveryQuotes: InterfaceGenericMap<InterfaceDeliveryQuote>;
  deliveryLocationHistory: InterfaceGenericMap<InterfaceDeliveryLocationHistory>;
  deliveryVehicleDrivers: InterfaceInterfaceDeliveryVehicleDrivers;
}

const initialState: DeliveryReducerState = {
  deliveryCancelTypes: {},
  deliveryLiveStatusTypes: {},
  deliveryScheduleWindowTypes: {},
  deliveryQuoteStatusTypes: {},
  deliveryStepTasksTypes: {},
  deliveries: {},
  deliverySteps: {},
  deliveryStepTasks: {},
  deliveryStatuses: {},
  deliveryStepTaskDocuments: {},
  deliveryParcels: {},
  deliveryAddresses: {},
  deliveryContacts: {},
  deliveryQuotes: {},
  deliveryLocationHistory: {},
  deliveryVehicleDrivers: {},
};

export const sliceName = 'delivery';

export const deliverySlice = createSlice({
  name: sliceName,
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(meLogOut.fulfilled, () => initialState)
      .addCase(getDeliveryCancelTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('deliveryCancelTypes'))
      .addCase(getDeliverLiveStatusTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('deliveryLiveStatusTypes'))
      .addCase(
        getDeliveryScheduleWindowTypes.fulfilled,
        sliceStateCheckAndUpdate.sliceItem('deliveryScheduleWindowTypes'),
      )
      .addCase(getDeliveryQuoteStatusTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('deliveryQuoteStatusTypes'))
      .addCase(getDeliveryStepTasksTypes.fulfilled, sliceStateCheckAndUpdate.sliceItem('deliveryStepTasksTypes'))
      .addCase(getCompanyDeliveries.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveries');
      })
      .addCase(getCompanyDeliveriesPage.fulfilled, (state, action) => {
        // Handle paginated response - extract data array from the response
        const paginatedData = { payload: action.payload.data };
        handleArrayOrMapPayload(state, paginatedData, 'deliveries');
      })
      .addCase(getDeliverySteps.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliverySteps');
      })
      .addCase(getDeliveryStepTasks.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryStepTasks');
      })
      .addCase(getCompanyDeliveryStatuses.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryStatuses');
      })
      .addCase(updateDeliveryStatus.fulfilled, sliceStateCheckAndUpdate.sliceItemElementUuid('deliveryStatuses'))
      .addCase(getCompanyDeliveryStepTaskDocuments.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryStepTaskDocuments');
      })
      .addCase(getDeliveryParcels.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryParcels');
      })
      .addCase(getDeliveryCompanyAddresses.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryAddresses');
      })
      .addCase(getDeliveryCompanyContacts.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryContacts');
      })
      .addCase(getDeliveryQuotes.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryQuotes');
      })
      .addCase(getDeliveryVehicleDrivers.fulfilled, sliceStateCheckAndUpdate.sliceItem('deliveryVehicleDrivers'))
      .addCase(getCompanyDeliveryLocationHistory.fulfilled, (state, action) => {
        handleArrayOrMapPayload(state, action, 'deliveryLocationHistory');
      })
      // New Delivery Management Endpoints
      .addCase(getDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(updateDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(deleteDelivery.fulfilled, (state, action) => {
        // Remove the delivery from the state
        if (action.meta.arg?.urlVarsReplaceMap) {
          const deliveryUuid = action.meta.arg.urlVarsReplaceMap['{deliveryUuid}'];
          if (deliveryUuid && state.deliveries[deliveryUuid]) {
            delete state.deliveries[deliveryUuid];
          }
        }
      })
      .addCase(duplicateDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      // Delivery Assignment handlers would update the delivery's vehicle/driver assignments
      .addCase(assignDeliveryToDriver.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(
        assignDeliveryToVehicle.fulfilled,
        sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'),
      )
      // Delivery lifecycle handlers update delivery status
      .addCase(startDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(completeDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(cancelDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'))
      .addCase(reopenDelivery.fulfilled, sliceStateCheckAndUpdate.sliceItemElementGeneric('deliveries', 'uuid'));
  },
});

export const getDeliveryCancelTypes = thunkGet.get(sliceName, 'type/delivery/cancel');
export const getDeliverLiveStatusTypes = thunkGet.get(sliceName, 'type/delivery/live/status');
export const getDeliveryScheduleWindowTypes = thunkGet.get(sliceName, 'type/delivery/schedule/window');
export const getDeliveryQuoteStatusTypes = thunkGet.get(sliceName, 'type/delivery/quote/status');
export const getDeliveryStepTasksTypes = thunkGet.get(sliceName, 'type/delivery/step/task');

export const getDeliverySteps = thunkGet.compUrl(sliceName, `/c/{companySlug}/admin/delivery/{deliveryUuid}/step`);
export const getDeliveryStepTasks = thunkGet.compUrl(sliceName, `/c/{companySlug}/admin/delivery/step/task`);
export const getDeliveryParcels = thunkGet.compUrl(sliceName, `/c/{companySlug}/admin/delivery/{deliveryUuid}/parcel`);
export const getDeliveryQuotes = thunkGet.compUrl(sliceName, `/c/{companySlug}/admin/delivery/quote`);
export const getCompanyDeliveries = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/delivery');

// Paginated version that can fetch more than 25 items
export const getCompanyDeliveriesPage = thunkGetPaginated.singlePage.compUrl<InterfaceDelivery>(
  sliceName,
  '/c/{companySlug}/admin/delivery',
);
export const getCompanyDeliveryStatuses = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/delivery/status');
export const getCompanyDeliveryStepTaskDocuments = thunkGet.compUrl(
  sliceName,
  '/c/{companySlug}/admin/delivery/step/task/doc',
);
export const getDeliveryCompanyAddresses = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/delivery/address');
export const getDeliveryCompanyContacts = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/delivery/contact');
export const getCompanyDeliveryLocationHistory = thunkGet.compUrl(
  sliceName,
  '/c/{companySlug}/admin/delivery/location',
);

export const getDeliveryVehicleDrivers = thunkGet.compUrl(sliceName, '/c/{companySlug}/admin/delivery/vehicle/driver');

export interface InterfaceUpdateDeliveryVehicleForm {
  vehicleUuids: string[];
}

export interface InterfaceDeliveryReplace {
  '{deliveryUuid}': string;
}

export const addDeliveryVehicle = thunkPost.compUrl<InterfaceUpdateDeliveryVehicleForm, InterfaceDeliveryReplace>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/vehicle',
  [getCompanyDeliveries(), getDeliveryVehicleDrivers()], // Get updated list of deliveries after change
);

export interface InterfaceUpdateDeliveryVehicleDriverForm {
  vehicleUuids: {
    [vehicleUuids: string]: {
      [vehicleUuids: string]: string[];
    };
  };
}

export const addDeliveryVehicleDriver = thunkPost.compUrl<
  InterfaceUpdateDeliveryVehicleDriverForm,
  InterfaceDeliveryReplace
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/vehicle/driver', [
  getCompanyDeliveries(),
  getCompanyDeliveryStatuses(),
  getDeliveryVehicleDrivers(),
  addSuccessNotification({
    title: 'Linked Vehicle and Driver',
    message: 'Linked Vehicle and Drive',
  }),
]);

export interface InterfaceUpdateDeliveryStatusForm {
  status: string;
}

export const addDeliveryStatusUpdate = thunkPost.compUrl<InterfaceUpdateDeliveryStatusForm, InterfaceDeliveryReplace>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/status',
  [getCompanyDeliveries()], // Get updated list of deliveries after change
);

export const updateDeliveryStatus = thunkPost.compUrl<
  { deliveryLiveStatus: string; lat: number; lng: number },
  { '{deliveryUuid}': string }
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/status', [
  getCompanyDeliveries(),
  getCompanyDeliveryStatuses(),
]);

export interface InterfaceUploadProofPhotoForm {
  deliveryStepTaskUuid: string;
  deliveryStepTaskProofTypeSlug: string;
  file: File;
}

export interface InterfaceUploadProofPhotoReplace {
  '{deliveryUuid}': string;
}

export const uploadDeliveryProofPhoto = thunkPost.compUrl<FormData, InterfaceUploadProofPhotoReplace>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/doc/step/task/proof',
  [getCompanyDeliveries(), getCompanyDeliveryStatuses(), getCompanyDeliveryStepTaskDocuments()],
);

export const selectDeliveryState = (state: AppState) => state.delivery;
export const selectDeliveries = (state: AppState) => selectDeliveryState(state).deliveries;
export const selectDelivery = (deliveryUuid: string | undefined) => (state: AppState) => {
  if (deliveryUuid !== undefined) {
    return selectDeliveries(state)[deliveryUuid];
  }
  return undefined;
};
export const selectDeliveriesActive = createSelector([selectDeliveries], (deliveries) =>
  Object.fromEntries(Object.entries(deliveries).filter(([, delivery]) => delivery.isActive)),
);
export const selectCompletedDeliveries = createSelector([selectDeliveries], (deliveries) =>
  Object.fromEntries(
    Object.entries(deliveries).filter(([, delivery]) => delivery.deliveryLiveStatusTypeSlug === 'completed'),
  ),
);
export const selectDeliveryLiveStatusTypes = (state: AppState) => selectDeliveryState(state).deliveryLiveStatusTypes;
export const selectDeliveryLiveStatusType =
  (deliveryLiveStatusTypeSlug: string | undefined) =>
  (state: AppState): InterfaceGenericTypeSlug | undefined => {
    if (!deliveryLiveStatusTypeSlug) {
      return undefined;
    }
    const statusTypes = selectDeliveryLiveStatusTypes(state);
    if (statusTypes == null) {
      return undefined;
    }
    return statusTypes[deliveryLiveStatusTypeSlug];
  };
export const selectAllDeliverySteps = (state: AppState) => selectDeliveryState(state).deliverySteps;
export const selectDeliveryStep =
  (deliveryStepUuid: string) =>
  (state: AppState): InterfaceDeliveryStep | undefined =>
    selectAllDeliverySteps(state)[deliveryStepUuid];

export const selectSingleDeliverySteps = (delivery: InterfaceDelivery | undefined) =>
  createSelector([selectAllDeliverySteps], (deliverySteps) => {
    if (delivery === undefined) {
      return [];
    }
    return delivery.deliverySteps
      .filter((deliveryStepSimple) => deliverySteps[deliveryStepSimple.deliveryStepUuid] !== undefined)
      .map((deliveryStepSimple) => deliverySteps[deliveryStepSimple.deliveryStepUuid]);
  });

export const selectDeliveryStepTasks = (state: AppState) => selectDeliveryState(state).deliveryStepTasks;
export const selectDeliveryStepTask =
  (deliveryStepTaskUuid: string) =>
  (state: AppState): InterfaceDeliveryStepTask | undefined =>
    selectDeliveryStepTasks(state)[deliveryStepTaskUuid];

export const selectSingleDeliveryStepTasks = (delivery: InterfaceDelivery | undefined) =>
  createSelector(
    [selectSingleDeliverySteps(delivery), selectDeliveryStepTasks],
    (deliverySteps, deliveryStepTasks): InterfaceGenericMap<InterfaceDeliveryStepTask> =>
      deliverySteps.reduce((collector: InterfaceGenericMap<InterfaceDeliveryStepTask>, deliveryStep) => {
        if (deliveryStep && deliveryStep.deliveryStepTasks) {
          deliveryStep.deliveryStepTasks.forEach((deliveryStepTaskSimple) => {
            collector[deliveryStepTaskSimple.deliveryStepTaskUuid] =
              deliveryStepTasks[deliveryStepTaskSimple.deliveryStepTaskUuid];
          });
        }
        return collector;
      }, {}),
  );

export const selectDeliveryAddresses = (state: AppState) => selectDeliveryState(state).deliveryAddresses;
export const selectDeliveryAddress =
  (addressUuid: string | undefined) =>
  (state: AppState): InterfaceAddress | undefined => {
    if (addressUuid === undefined) {
      return undefined;
    }
    return selectDeliveryAddresses(state)[addressUuid];
  };

export const selectAllDeliveryParcels = (state: AppState) => selectDeliveryState(state).deliveryParcels;
export const selectAllDeliveryStepTaskDocuments = (state: AppState) =>
  selectDeliveryState(state).deliveryStepTaskDocuments;
export const deliveryStepTasksTypes = (state: AppState) => selectDeliveryState(state).deliveryStepTasksTypes;

export const deliveryStepTasksTypeName =
  (deliveryStepTasksTypeSlug: string | undefined) =>
  (state: AppState): string | undefined => {
    if (!deliveryStepTasksTypeSlug) {
      return deliveryStepTasksTypeSlug;
    }

    const allTaskTypes = deliveryStepTasksTypes(state); // This is state.delivery.deliveryStepTasksTypes

    // Explicitly check if allTaskTypes is null or undefined.
    // If it is, we cannot look up the slug, so return the slug itself as a fallback.
    if (allTaskTypes == null) {
      // Using == null to catch both undefined and null
      return deliveryStepTasksTypeSlug;
    }

    // Defensive check: ensure allTaskTypes is an object before indexing.
    // This should be true if allTaskTypes is not null/undefined due to the check above
    // and assuming it's otherwise correctly structured or an empty object.
    if (typeof allTaskTypes !== 'object') {
      // This case should ideally not be hit if state is managed correctly.
      // console.error('deliveryStepTasksTypes(state) is not an object:', allTaskTypes);
      return deliveryStepTasksTypeSlug; // Fallback
    }

    const specificType = allTaskTypes[deliveryStepTasksTypeSlug];

    // Check if the specific type was found and has a name property.
    if (specificType != null && typeof specificType.name === 'string') {
      return specificType.name;
    }
    // Fallback to the slug if the type isn't found or has no name.
    return deliveryStepTasksTypeSlug;
  };

export const selectCompanyDeliveryStatuses = (state: AppState) => selectDeliveryState(state).deliveryStatuses;
export const selectCompanyDeliveryStatus =
  (deliveryUuid: string | undefined) =>
  (state: AppState): InterfaceDeliveryStatus | undefined => {
    if (!deliveryUuid) {
      return undefined;
    }
    const deliveryStatuses = selectCompanyDeliveryStatuses(state);
    if (deliveryStatuses == null) {
      return undefined;
    }
    return deliveryStatuses[deliveryUuid];
  };

// START: Added for adding person to step task
export interface InterfaceAddPersonToStepTaskForm {
  deliveryStepTaskUuid: string;
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string;
  phoneNumberCountryCode?: string;
  // If your API requires these, add them and update PersonAdd.tsx form & PersonFormData
  // extraData?: { [key: string]: any };
}

export const addPersonToDeliveryStepTask = thunkPost.compUrl<
  InterfaceAddPersonToStepTaskForm,
  { '{deliveryUuid}': string }
>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/add-contact-to-step-task', // Endpoint based on your description
  [
    // getDeliveryStepTasks(), // Optionally re-fetch tasks if the task object itself is updated with person info
    // getDeliveryCompanyContacts(), // Optionally re-fetch contacts if a new global contact is made
    addSuccessNotification({
      title: 'Contact Added to Task',
      message: 'Contact person has been successfully associated with the task.',
    }),
  ],
);
// END: Added for adding person to step task

export default deliverySlice.reducer;

// Only for initial load of data that doesn't require authentication
// This is only done to speed things up. It is not required.
// This data should be reloaded in the pages that need it.
// Load any data a page needs in the page. Else it will not update ever.
export const loadInitialDeliveryData = createDebouncedAsyncThunk(
  `${sliceName}/initial`,
  async (_, { dispatch }) => {
    dispatch(getDeliveryCancelTypes());
    dispatch(getDeliverLiveStatusTypes());
    dispatch(getDeliveryScheduleWindowTypes());
    dispatch(getDeliveryQuoteStatusTypes());
    dispatch(getDeliveryStepTasksTypes());
  },
  defaultTypeDebounceTimeMs,
);

// First, define the interface for the create delivery form data
export interface InterfaceCreateDeliveryForm {
  originatorAccountUuid?: string;
  deliveryQuoteStatusTypeSlug: string;
  deliveryLiveStatusTypeSlug: string;
  originatorOriginalQuoteId: string;
  currencyCode: string;
}

// Then add the thunkPost action

export const createDelivery = thunkPost.compUrl<InterfaceCreateDeliveryForm, object>(
  sliceName,
  '/c/{companySlug}/admin/delivery/create-delivery',
  [
    getCompanyDeliveries(),
    // addSuccessNotification({
    //   title: 'Mission Created',
    //   message: 'New mission has been created successfully',
    // }),
  ],
);

// Interface for creating a delivery step with an address
export interface InterfaceCreateDeliveryStepWithAddressForm {
  order: string;
  comment?: string;
  previousDeliveryStepUuid: null | string;
  address: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postCode: string;
    countryIsoCode: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
}

// Add this after your createDelivery thunk
export const createDeliveryStepWithAddress = thunkPost.compUrl<
  InterfaceCreateDeliveryStepWithAddressForm,
  { '{deliveryUuid}': string }
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/add-delivery-step-with-address', []);

// Interface for creating a parcel for a delivery step
export interface InterfaceCreateDeliveryParcelForm {
  originatorParcelId: string;
  heightCm: number;
  widthCm: number;
  depthCm: number;
  weightKg: number;
  priceMinor: number;
  currencyCode: string;
  parcelHandlingInstructions: {
    fragile: boolean;
    this_way_up: boolean;
    special_instructions?: string;
  };
}

// Interface for associating a person with a parcel
export interface InterfaceAddPersonToParcelForm {
  name: string;
  phoneNumber: string;
  email: string;
  notes?: string;
}

// Thunk for adding a person to a parcel
export const addPersonToParcel = thunkPost.compUrl<InterfaceAddPersonToParcelForm, { '{parcelUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/parcel/{parcelUuid}/add-contact',
  [
    addSuccessNotification({
      title: 'Contact Added',
      message: 'Contact has been added to parcel',
    }),
  ],
);

// Interface for adding a pickup or dropoff task to a delivery step
export interface InterfaceAddDeliveryStepTaskForm {
  deliveryStepUuid: string;
  order: number;
  notes?: string;
}

// Thunk for adding a pickup task to a delivery step
export const addDeliveryStepPickupTask = thunkPost.compUrl<
  InterfaceAddDeliveryStepTaskForm,
  { '{deliveryUuid}': string }
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/add-delivery-step-pickup', [
  // addSuccessNotification({
  //   title: 'Pickup Task Added',
  //   message: 'New pickup task has been added to delivery step',
  // }),
]);

// Thunk for adding a dropoff task to a delivery step
export const addDeliveryStepDropoffTask = thunkPost.compUrl<
  InterfaceAddDeliveryStepTaskForm,
  { '{deliveryUuid}': string }
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/add-delivery-step-dropoff', [
  addSuccessNotification({
    title: 'Dropoff Task Added',
    message: 'New dropoff task has been added to delivery step',
  }),
]);
// Interface for adding a parcel to a delivery step task
export interface InterfaceAddParcelToStepTaskForm {
  deliveryStepTaskUuid: string;
  originatorParcelId: string;
  heightCm: number;
  widthCm: number;
  depthCm: number;
  weightKg: number;
  items?: Array<{
    name: string;
  }>;
  handlingInstructions?: string[];
  notes?: string;
}

// Thunk for adding a parcel to a delivery step task
export const addParcelToDeliveryStepTask = thunkPost.compUrl<
  InterfaceAddParcelToStepTaskForm,
  { '{deliveryUuid}': string }
>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/add-parcel-to-step-task', []);

// ============= COMPREHENSIVE DELIVERY MANAGEMENT ENDPOINTS =============

// Delivery Management Interfaces
export interface InterfaceUpdateDeliveryForm {
  notes?: string;
  priority?: string;
  scheduledAt?: string;
  estimatedDuration?: number;
  specialInstructions?: string[];
}

export interface InterfaceDeliveryFilters {
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  driverId?: string;
  vehicleId?: string;
  priority?: string;
  limit?: number;
  offset?: number;
}

// Core Delivery CRUD Operations
export const getDelivery = thunkGet.compUrl<undefined>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}');

export const updateDelivery = thunkPut.compUrl<InterfaceUpdateDeliveryForm>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}',
);

export const deleteDelivery = thunkDelete.compUrl(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}');

export const duplicateDelivery = thunkPost.compUrl(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/duplicate',
);

// Delivery Assignment Management
export const assignDeliveryToDriver = thunkPost.compUrl<{ driverUuid: string }, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/assign-driver',
);

export const unassignDeliveryFromDriver = thunkDelete.compUrl<undefined, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/unassign-driver',
);

export const assignDeliveryToVehicle = thunkPost.compUrl<{ vehicleUuid: string }, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/assign-vehicle',
);

export const unassignDeliveryFromVehicle = thunkDelete.compUrl<undefined, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/unassign-vehicle',
);

// Delivery Status and Lifecycle Management
export const startDelivery = thunkPost.compUrl<undefined, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/start',
);

export const completeDelivery = thunkPost.compUrl<undefined, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/complete',
);

export const cancelDelivery = thunkPost.compUrl<{ reason?: string }, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/cancel',
);

export const reopenDelivery = thunkPost.compUrl<undefined, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/reopen',
);

// Delivery Route Management
export const optimizeDeliveryRoute = thunkPost.compUrl<{ deliveryUuids: string[] }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/optimize-route',
);

export const getDeliveryRoute = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/route',
);

export const updateDeliveryRoute = thunkPut.compUrl<{
  waypoints: Array<{ lat: number; lng: number; address?: string }>;
}>(sliceName, '/c/{companySlug}/admin/delivery/{deliveryUuid}/route');

// Delivery Proof Management
export const getDeliveryProof = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/proof',
);

export const deleteDeliveryProof = thunkDelete.compUrl(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/proof/{proofId}',
);

// Delivery Notes and Communication
export const addDeliveryNote = thunkPost.compUrl<{ note: string; isInternal?: boolean }, { '{deliveryUuid}': string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/note',
);

export const getDeliveryNotes = thunkGet.compUrl<undefined>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/note',
);

export const updateDeliveryNote = thunkPut.compUrl<{ note: string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/note/{noteId}',
);

export const deleteDeliveryNote = thunkDelete.compUrl(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/note/{noteId}',
);

// Delivery Analytics and Reporting
export const getDeliveryAnalytics = thunkGet.compUrl<InterfaceDeliveryFilters>(
  sliceName,
  '/c/{companySlug}/admin/delivery/analytics',
);

export const exportDeliveries = thunkPost.compUrl<{ format: 'csv' | 'pdf'; filters?: InterfaceDeliveryFilters }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/export',
);

// Delivery Time Estimates
export const getDeliveryTimeEstimate = thunkPost.compUrl<{
  pickupAddress: string;
  deliveryAddress: string;
  vehicleType?: string;
}>(sliceName, '/c/{companySlug}/admin/delivery/time-estimate');

export const updateDeliveryETA = thunkPut.compUrl<{ estimatedArrival: string }>(
  sliceName,
  '/c/{companySlug}/admin/delivery/{deliveryUuid}/eta',
);

// ==================== SMART SELECTOR FACTORY OPTIMIZATIONS ====================
// These selectors use the Smart Selector Factory for improved performance and reduced boilerplate

/**
 * Optimized Active Deliveries Selector
 * Replaces: Object.values(deliveries).filter(delivery => delivery.isActive !== false)
 * Benefits: Built-in memoization, automatic sorting, cleaner API
 */
export const selectActiveDeliveriesOptimized = createSmartSelector({
  select: selectDeliveries,
  filter: (delivery: InterfaceDelivery) => delivery.isActive !== false,
  sort: (a: InterfaceDelivery, b: InterfaceDelivery) => sortByDate(a, b),
});

/**
 * Optimized Completed Deliveries Selector
 * Replaces: Manual filtering by deliveryLiveStatusTypeSlug === 'completed'
 */
export const selectCompletedDeliveriesOptimized = createFilteredSelector(
  selectDeliveries,
  'deliveryLiveStatusTypeSlug',
  'completed',
);

/**
 * Optimized Deliveries by Status Selector
 * Parameterized selector for any status type
 */
export const selectDeliveriesByStatus = (status: string) =>
  createSmartSelector({
    select: selectDeliveries,
    filter: (delivery: InterfaceDelivery) => delivery.deliveryLiveStatusTypeSlug === status,
  });

/**
 * Optimized Active Deliveries Array
 * Converts object to sorted array - common pattern for table/list display
 */
export const selectActiveDeliveriesArray = createArraySelector(
  selectDeliveriesActive,
  (a: InterfaceDelivery, b: InterfaceDelivery) => sortByDate(a, b),
);

/**
 * Optimized Delivery Steps for Specific Delivery
 * Replaces: selectSingleDeliverySteps with better memoization
 */
export const selectDeliveryStepsOptimized = createSmartSelector({
  select: (state: AppState, deliveryUuid: string) => {
    const delivery = selectDelivery(deliveryUuid)(state);
    const allSteps = selectAllDeliverySteps(state);
    return { delivery, allSteps };
  },
  transform: ({ delivery, allSteps }) => {
    if (!delivery) {
      return [];
    }
    return delivery.deliverySteps
      .filter((stepSimple) => allSteps[stepSimple.deliveryStepUuid] !== undefined)
      .map((stepSimple) => allSteps[stepSimple.deliveryStepUuid])
      .sort((a, b) => a.stepOrderNo - b.stepOrderNo);
  },
});

/**
 * Count Active Deliveries
 * Efficient counting without creating intermediate arrays
 */
export const selectActiveDeliveriesCount = createSmartSelector({
  select: selectDeliveries,
  transform: (deliveries) =>
    Object.values(deliveries).filter((delivery: InterfaceDelivery) => delivery.isActive !== false).length,
});

/**
 * Delivery Status Summary
 * Groups deliveries by status for dashboard displays
 */
export const selectDeliveryStatusSummary = createSmartSelector({
  select: selectDeliveries,
  transform: (deliveries) => {
    const summary: Record<string, number> = {};
    Object.values(deliveries).forEach((delivery: InterfaceDelivery) => {
      const status = delivery.deliveryLiveStatusTypeSlug || 'unknown';
      summary[status] = (summary[status] || 0) + 1;
    });
    return summary;
  },
});

/**
 * Optimized Vehicle-Assigned Deliveries
 * Filters deliveries that have vehicle assignments
 */
export const selectDeliveriesWithVehicles = createSmartSelector({
  select: selectDeliveries,
  filter: (delivery: InterfaceDelivery) =>
    !!(delivery.vehicleMap?.vehicleUuids && Object.keys(delivery.vehicleMap.vehicleUuids).length > 0),
});

/**
 * Optimized Unassigned Deliveries
 * Filters deliveries that need vehicle/driver assignment
 */
export const selectUnassignedDeliveries = createSmartSelector({
  select: selectDeliveries,
  filter: (delivery: InterfaceDelivery) => {
    const hasVehicles = delivery.vehicleMap?.vehicleUuids && Object.keys(delivery.vehicleMap.vehicleUuids).length > 0;
    return delivery.isActive && !hasVehicles;
  },
  sort: (a: InterfaceDelivery, b: InterfaceDelivery) => sortByDate(a, b),
});

// ==================== MIGRATION HELPERS ====================
// These help migrate existing code to use optimized selectors

/**
 * Drop-in replacement for current selectActiveDeliveries
 * Can be used to gradually migrate existing code
 */
export const selectActiveDeliveriesCompat = (state: AppState) => selectActiveDeliveriesOptimized()(state);

/**
 * Drop-in replacement for current selectCompletedDeliveries
 * Can be used to gradually migrate existing code
 */
export const selectCompletedDeliveriesCompat = (state: AppState) => selectCompletedDeliveriesOptimized()(state);
